<template>

<div class="list-view">

	<div :class="showDetail ? 'sub-header detail-sub-header' : 'sub-header'">
		<div v-if="!showDetail" class="input-with-control">
			<input type="search" ref="search_term" v-model="filters.search_term" placeholder="Search..." @search="getList()" v-focus>
			<div class="button action input-go" @click="getList()"><i class="fas fa-search"></i></div>
		</div>
		<div v-if="showDetail" class="detail-title">{{ listDetail.contact.first_name }} {{ listDetail.contact.last_name }}</div>
		<div v-if="!showDetail" class="controls">
			<pagination :paging="paging" @updatePage="goToPage" />
		</div>
		<div></div>
		<div v-if="showDetail && listDetail.contact_membership_id" class="detail-title-meta"><label>ID: </label>{{ listDetail.membership.contact_membership_id }}</div>
	</div>

	<div class="filters" v-if="showFilters">

		<div v-if="!showDetail">

			<div class="title">Filter...</div>
			<label>Membership Type</label>
			<select v-model="filters.membership_id" @change="getList()">
				<option value="">Select...</option>
				<option v-for="(membership, index) in memberships" :value="membership.membership_id" :key="'membership_' + membership.membership_id">{{ membership.description }}</option>
			</select>
			<ul class="checkbox-group">
				<li v-if="subscriptionsEnabled">
				<label>Subscription</label>
				<select v-model="filters.subscription" @change="getList()">
					<option value="">Select...</option>
					<option value="yes">Has a subscription or prepaid</option>
					<option value="no">No subscription and not prepaid</option>
				</select>
				</li>
				<li v-if="subscriptionsEnabled">
					<label>Credit Card</label>
					<select v-model="filters.credit_card" @change="getList()">
						<option value="">Select...</option>
						<option value="expired">Is Expired</option>
						<option value="expires_soon">Expires soon</option>
						<option value="failed">Failed</option>
					</select>
				</li>
				<li>
					<label>Membership expired</label>
					<select v-model="filters.membership_expired" @change="getList()">
						<option value="">Select...</option>
						<option value="expired">Yes, membership has expired</option>
						<option value="soon">Membership expires soon</option>
						<option value="not_expired">No, membership has not expired</option>
					</select>
				</li>
				<li>
					<label>Has a Waiver on File</label>
					<select v-model="filters.waiver" @change="getList()">
						<option value="">Select...</option>
						<option value="yes">Yes, waiver on file</option>
						<option value="no">No waiver on file</option>
					</select>
				</li>
			</ul>
			<ul class="checkbox-group">
				<li>
					<span :class="['pointer', 'far', filters.inactive ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('inactive')"></span>
					<label>Include cancelled</label>
				</li>
			</ul>

			<div :class="['button', hasFilters ? 'neutral' : 'disabled']" @click="hasFilters ? clearFilters() : null">Clear Filters</div>

		</div>

		<div v-else>
			<div :class="['filter-list-button-large', { active : showDetailTab == 'member' }]" @click="fetchDetailTab('member')">Member Detail</div>
			<div :class="['filter-list-button-large', { disabled: !contact_membership_id || contact_membership_id == 'new' }, { active : showDetailTab == 'membership' }]" @click="fetchDetailTab('membership')">Membership Info</div>
			<div v-if="subscriptionsEnabled" :class="['filter-list-button-large', { disabled: !contact_membership_id || contact_membership_id == 'new' }, { active : showDetailTab == 'subscription' }]" @click="fetchDetailTab('subscription')">Subscription Info</div>
			<div :class="['filter-list-button-large', { disabled: !contact_membership_id || contact_membership_id == 'new' }, { active : showDetailTab == 'attachments' }]" @click="fetchDetailTab('attachments')">Waiver / Attachments</div>
			<div :class="['filter-list-button-large', { disabled: !contact_membership_id || contact_membership_id == 'new' }, { active : showDetailTab == 'account' }]" @click="fetchDetailTab('account')">Customer Account</div>
        </div>

		<div v-if="!showDetail">
			<div class="divider"></div>
			<div class="button action" @click="addMember()">Add Member</div>
			<div class="divider"></div>
			<div :class="['extra-menu-item', { disabled: gettingExport }]" @click="doExport()">Export CSV</div>
			<div v-if="legacyLink">
				<router-link :to="legacyLink" class="extra-menu-item">Legacy Version</router-link>
			</div>
			<div v-if="getDevEnvironment" class="extra-menu-item" @click="getList()">Reload List</div>
			<div class="message">{{ exportMessage }}</div>
		</div>

		<div v-if="showDetail">
			<button class="button action" @click="saveItem()">Save {{ showDetailTab }}</button>
			<div class="divider"></div>
			<button class="button neutral" :class="{ disabled: toggleButton}" :disabled="toggleButton" @click="backToList()">Back To List</button>
		</div>

	</div>

	<loading v-if="loading" class="loading"></loading>
	
	<div v-else-if="showDetail" class="detail">

		<!--Member Detail Tab-->
		<div v-if="showDetailTab == 'member'" class="detail">
			<div class="section">
				<h2>Contact Info</h2>
				<contact-detail-form :clientId="listDetail.contact.client_id" :contact="listDetail.contact" :emailRequired="true" :hideControls="true" @update="setListDetail" />
			</div>
		</div>

		<!--Membership Detail Tab-->
		<div v-if="showDetailTab == 'membership'" class="two-columns detail" style="grid-template-columns: 1fr 23em;">
			<div class="section">
				<h2>Membership Info</h2>
				<div v-if="listDetail.membership.inactive" class="note">
					This membership type has been deactivated!<br>
					The member info is still available, but any subscription has been suspended.
				</div>
				<div class="two-columns">
					<div>
						<label-required :label="'Membership Type'" :condition="listDetail.membership.membership_id" />
						<select v-model="listDetail.membership.membership_id" @change="showConfirmChangeMembershipType = true">
							<option value="">Select...</option>
							<option v-for="(membership, index) in memberships.filter(m => m.inactive === 0)" :value="membership.membership_id" :key="'membership_' + membership.membership_id">{{ membership.description }}</option>
						</select>
						<div class="two-columns">
							<div>
								<label>Price</label>
								<p class="detail-data">{{ listDetail.membership.prices.pos }}</p>
							</div>
							<div>
								<label>Frequency</label>
								<p class="detail-data">{{ membershipFromSelect && membershipFromSelect.subscription_frequency_code ? membershipFromSelect.subscription_frequency_code.capitalizeWords() : 'None' }}</p>
							</div>
						</div>
						<label>Range Discounts</label>
						<p v-if="listDetail.membership.range_discount == '1.00'" class="detail-data">Member does not pay for range time.</p>
						<p v-else-if="listDetail.membership.range_amount" class="detail-data">Member pays {{ listDetail.membership.range_amount }} for range time.</p>
						<p v-else-if="listDetail.membership.range_discount > 0"  class="detail-data">Member gets a {{ listDetail.membership.range_discount * 100 }}% discount on range time.</p>
						<p v-else class="detail-data">Member pays full price for range time.</p>
						
						<div v-if="false">
							<label>Product Discounts</label>
							<p v-if="listDetail.membership.product_discount" class="detail-data">Member gets a {{ listDetail.membership.product_discount * 100 }}% discount on products.</p>
							<p v-else class="detail-data">Member does not get a discount on products.</p>
						</div>

						<div class="v-spaced">
							<router-link :to="'/range-memberships/memberships/' + listDetail.membership.membership_id" class="button neutral inline">Manage This Membership Type</router-link>
						</div>

					</div>
					<div>
						<label>Member Number</label>
						<input type="text" v-model="listDetail.membership.membership_number" />
						<label class="full">Membership Start Date</label>
                        <div class="input-with-tool-tip">
                            <input type="date" :max="listDetail.membership.end_date" v-model="listDetail.membership.start_date" style="width: 9em;">
                            <tool-tip left='-10' content='This membership is not considered active until on or after the start date.'/>
                        </div>
						<label class="full">Membership End Date</label>
                        <div class="input-with-tool-tip">
                            <input type="date" :min="listDetail.membership.start_date" v-model="listDetail.membership.end_date" style="width: 9em;">
                            <tool-tip left='-10' content='After the end date, this membership will not be considered active and any subscription payments will not be processed.'/>
                        </div>
						<div v-if="listDetail.membership.membership_id" class="v-spaced">
							<div class="button neutral inline" @click="showCancelMembership = true">{{ listDetail.membership.membership_inactive ? 'Reactivate' : 'Cancel' }} This Membership</div>
						</div>
					</div>
				</div>

				<div style="margin: 2em 0 0 0;">
					<h2>Additional Customers<span v-if="listDetail.membership.max_customers" class="note"> (max {{ listDetail.membership.max_customers - 1 }})</span></h2>
					<div v-if="listDetail.membership.primary_contact_membership_id">
						<p><router-link :to="'/range-memberships/members/' + listDetail.membership.primary_contact_membership_id">This customer is a member of another membership...</router-link></p>
					</div>
					<div v-else>
						<div v-if="listDetail.additional_members && listDetail.additional_members.length > 0">
							<div v-for="member in listDetail.additional_members" class="three-one-columns clickable-list-item">
								<div class="two-columns" @click="$router.push('/range-memberships/members/' + member.contact_membership_id)">
									<div>
										<label>Customer</label><br>
										{{ member.first_name }} {{ member.last_name }}<br>
										{{ member.address_1 }}<br>
										{{ member.city }} {{ member.state}} {{ member.zip_code }}
									</div>
									<div>
										<label>Phone / Email</label><br>
										{{ member.phone_number }}<br>
										{{ member.email_address }}
									</div>
								</div>
								<div>
									<div class="button neutral inline" @click="removeCustomerMembership = member">Remove</div>
								</div>
							</div>
						</div>
						<div v-else>
							<p>No additional customers on this membership</p>
						</div>
						<div style="margin: 1em 0;">
							<div :class="['button', listDetail.membership.max_customers && listDetail.additional_members.length >= listDetail.membership.max_customers - 1 ? 'disabled' : 'neutral', 'inline']" @click="addAdditionalMember()">Add A Customer</div>
						</div>
					</div>
				</div>

			</div>
			<div class="section">
				<h2>Status</h2>
				<p>This is your member's current Membership information.</p>

				<label class="full">Subscription Type</label>
				<p class="detail-data">
					<span v-if="listDetail.subscription.subscription_id">{{ listDetail.subscription.prepaid ? 'Prepaid' : isNonRecurringSubscription(listDetail.subscription.subscription_frequency_code) ? 'None' : 'Credit Card' }}</span>
					<span v-else>No Subscription</span>
				</p>

				<div class="two-columns">
					<div>
						<label class="full">Membership Start</label>
						<p class="detail-data">
							<span v-if="listDetail.membership.start_date">{{ $moment(listDetail.membership.start_date).format('MM/DD/YYYY') }}</span>
							<span v-else>No start date</span><br>
						</p>
						<label class="full">Membership End</label>
						<p class="detail-data">
							<span v-if="listDetail.membership.end_date">End {{ $moment(listDetail.membership.end_date).format('MM/DD/YYYY') }}</span>
							<span v-else>No end date</span>
						</p>
					</div>
					<div>
						<div style="padding: 0.5em; background-color: #ededed;">
							NOTE: a member's Subscription payment can have a different start date.
						</div>
					</div>
				</div>

			</div>
		</div>
		
		<!--Subscription Detail Tab-->
		<div v-if="showDetailTab == 'subscription'" class="two-columns detail" style="grid-template-columns: 1fr 23em;">
			<div class="section">
				<payment-inputs :billing="listDetail.subscription" :showTypeControls="true" @doAction="getItem" :key="'payment_inputs_' + listDetail.contact_membership_id" />
			</div>
			<div class="section">
				<div class="disabled-pane" v-if="toggleButton"></div>
				<h2>Status</h2>
				This is your member's current subscription info.<br><br>
				<label class="full">Subscription Amount</label>
				<p class="detail-data">
					<span v-if="(isNonRecurringSubscription(membershipFromSelect && membershipFromSelect.subscription_frequency_code ? membershipFromSelect.subscription_frequency_code : '') || listDetail.membership.membership_inactive  || listDetail.subscription.prepaid)">No Subscription</span>
					<span v-else-if="listDetail.subscription.amount">${{ listDetail.subscription.amount }}</span>
				</p>
				<label>Frequency</label>
				<p class="detail-data">{{ (!listDetail.membership.membership_inactive || listDetail.subscription.prepaid) ? (membershipFromSelect && membershipFromSelect.subscription_frequency_code ? membershipFromSelect.subscription_frequency_code.capitalizeWords() : '') : 'None' }}</p>
				<label class="full">Card Status</label>
				<p class="detail-data">
					<template v-if="listDetail.subscription.subscription_id">
						<span v-if="listDetail.subscription.vault_id == 'PREPAID'">Prepaid</span>
						<span v-else-if="listDetail.subscription.expiration_year && listDetail.subscription.expiration_year + '-' + listDetail.subscription.expiration_month + '-30' < today">Expired</span>
						<span v-else-if="listDetail.subscription.expiration_year && listDetail.subscription.expiration_year + '-' + listDetail.subscription.expiration_month + '-30' < $moment(today).add(1, 'M').format('YYYY-MM-DD')">Expires Soon</span>
						<span v-else-if="listDetail.subscription.internal_use_only == 1">Payment Failed</span>
						<span v-else>Card is current</span>
					</template>
					<template v-else>{{listDetail.subscription.prepaid ? 'Prepaid' : 'No card on file'  }}</template>
				</p>
				<div v-if="!listDetail.subscription.prepaid && listDetail.subscription.vault_id != 'PREPAID' && listDetail.subscription.amount > 0">
					<label class="full">Next Scheduled Payment</label>
					<p v-if="(isNonRecurringSubscription(membershipFromSelect && membershipFromSelect.subscription_frequency_code ? membershipFromSelect.subscription_frequency_code : '') || listDetail.membership.membership_inactive  || listDetail.subscription.prepaid)" class="detail-data">None</p> 
					<p v-else-if="listDetail.subscription.next_payment_date" class="detail-data">{{ $moment(listDetail.subscription.next_payment_date).format('MM/DD/YYYY') }} </p>
					<div v-if="listDetail.subscription.vault_id" style="padding: 0.5em 0 0 0;">
    	                <div class="button neutral small" @click="confirmDoPayment(listDetail.subscription)">Process A Payment Now</div>
	                </div>

				</div>
			</div>

			<div class="section" style="grid-column: 1/3;">
				<div class="disabled-pane" v-if="toggleButton"></div>
				<h2>Payment History</h2>

				<div v-if="orderPayments.length == 0 && subscriptionPayments.length == 0" class="empty-list">
					No payment history for this member
				</div>

				<table v-if="orderPayments.length > 0" class="data-list payment-list" cellpadding="0" cellspacing="0" style="margin: 0 0 1em 0;">
					<thead>
						<tr>
							<th class="list">Date</th>
							<th class="list">Order Number</th>
							<th class="list">Description</th>
							<th class="list">Item</th>
							<th class="list">Amount</th>
							<th class="list"></th>
						</tr>
					</thead>
					<tr v-for="(payment, index) in orderPayments" class="list">
						<td class="list">{{ $moment(payment.order_date).format('MM/DD/YYYY') }}</td>
						<td class="list">{{ payment.order_number }}</td>
						<td class="list">Membership Purchase</td>
						<td class="list">{{ payment.description }}</td>
						<td class="list">${{ payment.order_price }}</td>
						<td class="list right">
							<div class="button neutral inline" style="width: 12em; margin: 0.5em 0;" @click="$router.push('/orders-sales/orders/detail/' + payment.order_id)">View Order</div>
						</td>
					</tr>
				</table>

				<table v-if="subscriptionPayments.length > 0" class="data-list payment-list" cellpadding="0" cellspacing="0">
					<thead>
						<tr>
							<th class="list">Date</th>
							<th class="list">Description</th>
							<th class="list">Card</th>
							<th class="list">Amount</th>
							<th class="list">Transaction ID</th>
							<th class="list">Auth Code</th>
							<th class="list center">Status</th>
							<th class="list"></th>
						</tr>
					</thead>
					<tr v-for="(payment, index) in subscriptionPayments" class="list">
						<td class="list">{{ $moment(payment.payment_date).format('MM/DD/YYYY') }}</td>
						<td class="list">{{ payment.description }}</td>
						<td class="list">{{ payment.reference_number }}</td>
						<td class="list">${{ payment.amount }}</td>
						<td class="list">{{ payment.transaction_identifier }}</td>
						<td class="list">{{ payment.authorization_code }}</td>
						<td class="list center"><i :class="['fa', payment.response_code == '100' && payment.authorization_code ? 'fa-check' : 'fa-times error']"></i></td>
						<td v-if="showPaymentButton(payment)" class="list right">
							<div :class="['button', saving ? 'disabled' : 'neutral', 'inline']" style="width: 12em; margin: 0.5em 0;" @click="confirmDoPayment(payment)">{{ payment.response_code == '100' && payment.authorization_code ? "Refund" : "Retry" }}</div>
						</td>
						<td v-else class="list right" style="font-size: 0.8em; width: 5em; height: 2.75em;">{{ showPaymentLabel(payment) }}</td>
					</tr>
				</table>

			</div>
		</div>

		<!--Waivers and Attachments Detail Tab-->
		<div v-if="showDetailTab == 'attachments'" class="detail">
			<div class="section">
				<h2>Waivers / Applications</h2>
				<div v-if="waivers.length > 0">
					<div v-for="waiver in waivers" class="three-columns clickable-list-item" @click="showWaiver(waiver)">
						<div>
							<label>Waiver Date</label><br>
							{{ $formatDate(waiver.waiver_date) }}
						</div>
						<div>
							<label>Waiver Description</label><br>
							{{ waiver.description }}
						</div>
						<div>
							<label>Waiver Type</label><br>
							{{ waiver.waiver_type_code.capitalizeWords() }}
						</div>
					</div>
				</div>
				<div class="three-columns" style="margin: 1em 0 0 0;">
					<div>
						<checkbox v-model="waiverOnFile" label="Check if a printed waiver is on file" :key="'waiver_on_file_' + waiverOnFile" />
					</div>
					<div>
						<label>Printed Waiver Date</label> <input type="date" v-model="waiverOnFileDate" style="width: 10em;" />
					</div>
					<div>
						<div class="button neutral inline" @click="saveWaiverDate()">Save Waiver Date</div>
					</div>
				</div>
				<div v-if="listDetail.membership.terms" style="margin: 1em 0 0 0;">
					<span class="pointer" @click="showTermsDetail = true">View membership terms...</span>
				</div>
				<h2 style="margin: 1em 0 0 0;">Attachments</h2>				
				<div class="two-one-columns">
					<div class="border-right">
						<h3 class="attachment-title">Current attachments</h3>
						<div v-if="attachments.length > 0">
							<div v-for="(file, index) in attachments" class="one-two-columns" style="position: relative; margin: 0 1em 1em 1em; padding: 1em 0 0 0; border-top: 1px solid #ccc;">
								<div class="attachment-preview">
									<img v-if="file.file_preview_url" :src="file.file_preview_url">
									<div v-else>No Preview<br>Available</div>
								</div>
								<div class="attachment-details">
									<div class="detail-group">
										<label>File Name</label>
										<p>{{ file.file_name }}</p>
									</div>
									<div class="detail-group">
										<label>File Type</label>
										<p>{{ file.file_type }}</p>
									</div>
									<div class="detail-group">
										<label>Attached Date / Time</label>
										<p>{{ file.attached_time }}</p>
									</div>
									<button class="detail-group" @click="downloadContactAttachment(file.file_name, file.contact_attachment_id)" style="background-color: #4a4a4a;">Download</button>
								</div>
								<div class="button neutral inline" style="position: absolute; top: 0.5em; right: 0;" @click="confirmRemoveFile(file.contact_attachment_id)">
									<i class="fa fa-trash"></i>
								</div>
							</div>
						</div>
						<div v-else class="empty-list">
							No files are attached<br>to this customer
						</div>
					</div>
					<div>
						<h3 class="attachment-title">Add an attachment</h3>
						<div>
							<vue-dropzone @vdropzone-file-added="filesFileAdded" @vdropzone-success="filesUploadSuccess" @vdropzone-error="filesUploadError" ref="filesDropzone" id="site_background_dropzone" :options="filesDropzoneOptions"></vue-dropzone>
							<label><em>Allowed file types: pdf, jpg, png</em></label>
							<div class="dropzone dz-preview dz-file-preview uploaded-files" style="border: 0 none;"></div>
						</div>
					</div>
				</div>

				<div v-if="attachmentsMessage" class="message-overlay">
					<p v-html="attachmentsMessage"></p>
				</div>
			</div>
		</div>

		<!--Account Detail Tab-->
		<div v-if="showDetailTab == 'account'" class="two-columns detail" style="grid-template-columns: 1fr 23em;">
			<div class="section">
				<h2>Customer Account</h2>
				<user-inputs :user="listDetail.user" />
			</div>
			<div class="section">
				<h2>Status</h2>
				<div v-if="listDetail.user.user_id">
					<p>Your member's current customer account info.</p>
					<div class="note">A customer account lets members log into your website to manage their membership and orders. Making any changes here will also change the <a href="/dashboard/users/user-type-group/customers" target="_blank">Customer Account</a> record.</div>
				</div>
				<div v-else>
					<p>This member does not have a customer account.</p>
					<div class="note">A customer account lets members log into your website to manage their membership and orders. You can create an account for them and send an email to invite this member to log in to their new account...</div>
					<div class="button neutral inline" @click="confirmInviteMember()">Create Customer Account</div>
				</div>
			</div>
		</div>

	</div>

	<div v-else class="list">

	    <div v-if="list.length == 0" class="empty-list">
	        This list is empty
	    </div>

	    <table v-else class="data-list member-list" cellpadding="0" cellspacing="0">
			<thead>
				<tr>
					<th></th>
					<th class="list">Member</th>
					<th class="list">Phone / Email</th>
					<th class="list">Membership</th>
					<th class="list">Start / End</th>
				</tr>
			</thead>
			<tr v-for="(member, index) in list" class="clickable-list-item" @click="getItemRoute(member.contact_membership_id)">
				<td :class="['status-cell', statusClass(member)]">
					<div class="status-expanded">
						<span class="status">{{ statusClass(member).capitalizeWords() }}</span>
					</div>
				</td>
				<td class="list">
					{{ member.full_name }}<br>
					{{ member.address_1 }}<br>
					{{ member.city }} {{ member.state}} {{ member.zip_code }}<br>

				</td>
				<td class="list">
					{{ member.phone_number }}<br>
					{{ member.email_address }}
				</td>
				<td class="list">
					{{ member.description }}<br>
					<label>Number</label> {{ member.membership_number || 'N/A' }}
				</td>
				<td class="list">
					<div class="one-three-columns" style="grid-template-columns: 1.25em 1fr;">
						<label>Start</label> {{ member.start_date ? $moment(member.start_date).format('MM/DD/YYYY') : 'N/A' }}<br>
						<label>End</label> {{ member.end_date ? $moment(member.end_date).format('MM/DD/YYYY') : 'N/A' }}
					</div>
				</td>
			</tr>
		</table>

	</div>

    <message-modal v-if="message" :message="message" :loading="saving" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" @action="doMessageModalAction()" />

	<iframe id="download" src="" style="display: none;"></iframe>

	<waiver-detail v-if="showWaiverDetail.waiver_id" :showWaiverDetail="showWaiverDetail" @close="showWaiverDetail = {}" />

	<div v-if="showTermsDetail" class="modal large-modal">
		<div class="inner">
			<h2>Terms</h2>
			<div class="content" ref="terms_content">
				<h1>Memebership Terms & Conditions</h1>
				<div style="margin: 0 0 1em 0;">
					<h3>Member Info</h3>
					<table style="width: 100%;">
						<tr>
							<td>
								{{ listDetail.contact.first_name }} {{ listDetail.contact.last_name }}<br>
								{{ listDetail.contact.address_1 }}<br>
								{{ listDetail.contact.city }} {{ listDetail.contact.state }} {{ listDetail.contact.zip_code }}<br>
							</td>
						</tr>
					</table>
				</div>
				<h3>Terms</h3>
				<div v-html="listDetail.membership.terms"></div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showTermsDetail = false">Cancel</div>
				<div class="button neutral spaced" @click="printTerms()">Print</div>
			</div>
		</div>
	</div>

	<div v-if="showAddMember" class="modal medium-modal">
		<div class="inner">
			<h2>Add a Member</h2>
			<div class="content">
				<label-required :label="'Membership Type'" :condition="addCustomer.membership_id" />
				<select v-model="addCustomer.membership_id" v-focus>
					<option value="">Select...</option>
					<option v-for="(membership, index) in memberships" :value="membership.membership_id" :key="'add_membership_' + membership.membership_id">{{ membership.description }}</option>
				</select>
				<p style="margin: 1em 0;">You can add this membership to one of your existing customers...</p>
				<div class="input-with-control">
					<input type="search" ref="search_term" v-model="addCustomer.search_term" placeholder="Search customers..." @search="searchCustomers()">
					<div :class="['button', addCustomer.membership_id ? 'action' : 'disabled', 'input-go']" @click="searchCustomers()"><i class="fas fa-search"></i></div>
				</div>
				<div class="modal-search-result">
					<table class="clickable-list">
						<tr v-for="(contact, index) in customerSearchList" class="clickable-list-item" @click="newMemberFromContactId(contact.contact_id)">
							<td>{{ contact.full_name }}</td>
							<td>{{ contact.email_address }}</td>
							<td>{{ contact.phone_number }}</td>
						</tr>
					</table>
				</div>
				<div v-if="modalMessage" class="message">{{ modalMessage }}</div>
				<p style="margin: 1em 0;">Or you can create a new customer...</p>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="backToList()">Cancel</div>
				<div v-if="$route.query.new_member_from_contact_id" :class="['button', addCustomer.membership_id ? 'neutral' : 'disabled', 'spaced']" @click="newMemberFromContactId($route.query.new_member_from_contact_id)">Create Member</div>
				<div v-else :class="['button', addCustomer.membership_id ? 'neutral' : 'disabled', 'spaced']" @click="newMemberFromContactId('new')">Create New Customer</div>
			</div>
		</div>
	</div>

	<div v-if="showConfirmChangeMembershipType" class="modal">
		<div class="inner">
			<h2>Change Membership</h2>
			<div class="content">
				<p v-if="listDetail.membership.membership_id">
					Are you sure you want to change {{ listDetail.contact.first_name}} {{ listDetail.contact.last_name}}'s<br>
					membership to {{ membershipFromSelect.description }}?<br><br>
					New subscription will be ${{ membershipFromSelect.prices.pos }} / {{ (membershipFromSelect && membershipFromSelect.frequency_code) ? membershipFromSelect.frequency_code.capitalizeWords() : '' }}
				</p>
				<p v-else>Please select a membership type.</p>
				<div v-if="modalMessage" class="message">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="cancelChangeMembership()">Cancel</div>
				<div :class="['button', !listDetail.membership.membership_id || saving ? 'disabled' : 'neutral', 'spaced']" @click="changeMembershipType()">Yes, change membership</div>
			</div>
		</div>
	</div>

	<div v-if="showCancelMembership" class="modal">
		<div class="inner">
			<h2>{{ listDetail.membership.membership_inactive ? 'Reactivate' : 'Cancel' }} Membership</h2>
			<div class="content">
				<p>Are you sure you want to {{ listDetail.membership.membership_inactive ? 'reactivate' : 'cancel' }} {{ listDetail.contact.first_name}} {{ listDetail.contact.last_name}}'s membership?</p>
				<p v-if="listDetail.subscription.subscription_id">Their subscription payment will also be {{ listDetail.membership.membership_inactive ? 'reactivated' : 'cancelled' }}.</p>
				<div v-if="modalMessage" class="message">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showCancelMembership = false">Cancel</div>
				<div :class="['button', saving ? 'disabled' : 'neutral', 'spaced']" @click="saveCancelMembership()">Yes, {{ listDetail.membership.membership_inactive ? 'reactivate' : 'cancel' }} membership</div>
			</div>
		</div>
	</div>

	<div v-if="showDoPayment" class="modal">
		<div class="inner">
			<h2>{{ paymentDetail.heading }}</h2>
			<div class="content">
				<div class="two-columns" style="margin: 0 0 1em 0;">
					<div>
						<label class="full"><span v-if="paymentDetail.do != 'process'">Original </span>Date</label>
						<b>{{ $moment(paymentDetail.payment_date).format('MM/DD/YYYY') }}</b>
					</div>
					<div>
						<label class="full">Amount</label>
						<b>${{ paymentDetail.amount }}</b>
						<span v-if="!listDetail.membership.not_taxable"> (plus tax)</span>
					</div>
				</div>
				<p>Are you sure you want to {{ paymentDetail.do }} this payment?</p>
				<div v-if="modalMessage" class="message">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showDoPayment = false">Cancel</div>
				<div :class="['button', saving ? 'disabled' : 'neutral', 'spaced']" @click="doPayment()">Yes, {{ paymentDetail.do }} payment</div>
			</div>
		</div>
	</div>

	<div v-if="showInviteMember" class="modal">
		<div class="inner">
			<h2>Send Invite Email</h2>
			<div class="content">
				<div v-if="$isValidEmailAddress(listDetail.contact.email_address)">
					<p>This will create a new customer account for <b>{{ listDetail.contact.first_name }} {{ listDetail.contact.last_name }}</b> and send an email to <b>{{ listDetail.contact.email_address }}</b> inviting them to log in to their account with a link to the My Account page of your website.</p>
					<label>Username</label>
					<input type="text" v-model="inviteMemberData.user_name"/>
					<label>Temporary Password <i>they can change this once they log in</i></label>
					<input type="text" v-model="inviteMemberData.password"/>
				</div>
				<p v-else>This member doesn't have a valid email address.</p>
				<div v-if="modalMessage" class="message">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showInviteMember = false">Cancel</div>
				<div :class="['button', saving || !$isValidEmailAddress(listDetail.contact.email_address) || !inviteMemberData.user_name || !inviteMemberData.password ? 'disabled' : 'neutral', 'spaced']" @click="inviteMember()">Create Customer Account</div>
			</div>
		</div>
	</div>

	<div v-if="showAddAdditionalMember" class="modal medium-modal">
		<div class="inner">
			<h2>Add a Customer to this Membership</h2>
			<div v-if="this.listDetail.membership.max_customers && listDetail.membership.max_customers && this.listDetail.additional_members.length >= this.listDetail.membership.max_customers - 1" class="content">
				<p>This membership type allows {{ listDetail.membership.max_customers - 1 }} additional customer{{ listDetail.membership.max_customers > 2 ? 's' : '' }}.</p>
				<p>You can change this limit in the <router-link to="/range-memberships/memberships">Membership UPC Setup</router-link> program.</p>
			</div>
			<div v-else class="content">
				<p style="margin: 1em 0;">You can add existing customers to this membership...</p>
				<div class="input-with-control">
					<input type="search" ref="search_term" v-model="addCustomer.search_term" placeholder="Search customers..." @search="searchCustomers()">
					<div :class="['button', addCustomer.search_term ? 'action' : 'disabled', 'input-go']" @click="searchCustomers()"><i class="fas fa-search"></i></div>
				</div>
				<div class="modal-search-result">
					<table class="clickable-list">
						<tr v-for="(contact, index) in customerSearchList" class="clickable-list-item" @click="addAdditionalMemberFromContactId(contact.contact_id)">
							<td>{{ contact.full_name }}</td>
							<td>{{ contact.email_address }}</td>
							<td>{{ contact.phone_number }}</td>
						</tr>
					</table>
				</div>
				<div v-if="modalMessage" class="message">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="customerSearchList = []; showAddAdditionalMember = false">Cancel</div>
			</div>
		</div>
	</div>

	<div v-if="removeCustomerMembership" class="modal">
		<div class="inner">
			<h2>Remove Customer</h2>
			<div class="content">
				<p>Are you sure you want to remove <b>{{ removeCustomerMembership.first_name }} {{ removeCustomerMembership.last_name }}</b> from this membership?</p>
				<div v-if="modalMessage" class="message">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="removeCustomerMembership = false">Cancel</div>
				<div :class="['button', saving ? 'disabled' : 'neutral', 'spaced']" @click="removeCustomer()">Yes, remove</div>
			</div>
		</div>
	</div>

	<help-popup :helpLink="{400:'Manage Members', 401:'Member Detail', 406:'Range Setup - Open Lanes', 409:'Selling Memberships at POS', 60:'Online Membership Application'}" :tours="false"></help-popup>

</div>

</template>

<script>
import Loading from '@/components/Loading.vue'
import ContactDetailForm from '@/components/ContactDetailForm.vue'
import LabelRequired from '@/components/LabelRequired.vue'
import BinaryToggle from '@/components/BinaryToggle.vue'
import Checkbox from '@/components/Checkbox.vue'
import DateInput from '@/components/DateInput.vue'
import PaymentInputs from '@/components/PaymentInputs.vue'
import UserInputs from '@/components/UserInputs.vue'
import WaiverDetail from '@/components/WaiverDetail'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: 'members',
	props: ['contact_membership_id'],
    components: {
        'loading': Loading,
        'contact-detail-form': ContactDetailForm,
		'label-required': LabelRequired,
		'binary-toggle': BinaryToggle,
		'checkbox': Checkbox,
        'date-input': DateInput,
		'payment-inputs': PaymentInputs,
		'user-inputs': UserInputs,
		'waiver-detail': WaiverDetail,
		'vue-dropzone': vue2Dropzone,
		'help-popup': HelpPopup
    },
	data() {
		return {
            loading: false,
			saving: false,
			message: '',
			messageStatus: '',
			messageTimeout: 0,
			showFilters: true,
			filters: {},
			filterMessage: '',
			filterLoading: false,
			memberships: [],
			list: [],
			paging: {},
			showDetail: false,
			showDetailTab: '',
			listDetail: {},
			listIndex: '',
			subscriptionsEnabled: false,
			subscriptionType: 'CC',
			subscriptionDays: [],
			subscriptionPayments: [],
			orderPayments: [],
			gettingExport: false,
			exportMessage: '',
			showConfirmChangeMembershipType: false,
			changingMembership: '',
			modalMessage: '',
			showAddMember: false,
			waivers: [],
			waiverOnFile: false,
			waiverOnFileDate: '',
			showWaiverDetail: {},
			showAttachments: false,
			attachments: [],
			attachmentsMessage: '',
			addCustomer: {
				search_term: '',
				membership_id: ''
			},
			customerSearchList: [],
			today: this.$moment().format('YYYY-MM-DD'),
			showCancelMembership: false,
			showDoPayment: false,
			paymentDetail: {},
			legacyLink: '',
			showInviteMember: false,
			additionalMembers: [],
			showAddAdditionalMember: false,
			removeCustomerMembership: false,
			showTermsDetail: false,
			inviteMemberData: {},
			editLocked: true,
			toggleButton: false
		}
	},
	created() {
		for (let day = 1; day < 31; day++ ) {
			this.subscriptionDays.push(day.toString().padStart(2, '0'))
		}
		this.getListMemberships()
		this.clearFilters()
	},
	computed: {
		hasFilters() {
			// iterate the filters object and return true if any element has value except page
			for (let key in this.filters) {
				if (key != 'page' && this.filters[key]) {
					return true
				}
			}
		},
		membershipFromSelect() {
			return this.memberships.filter((membership) => membership.membership_id == this.listDetail.membership.membership_id)[0]
		},
		frequencyFromSelect() {
			return this.subscriptionFrequencies.filter((frequency) => frequency.subscription_frequency_id == this.listDetail.membership.subscription_frequency_id)[0]
		},
		membershipStatus() {
			if (this.listDetail.membership.end_date && this.listDetail.end_date < this.today) { return 'Membership Expired' }
			if (this.listDetail.subscription_suspended) { return 'Failed' }
			if (this.listDetail.subscription_id) { return 'Active' }
		},
		filesDropzoneOptions() {
			return {
				url: this.getApiEndpoint + '?action=upload_contact_attachments',
                acceptedFiles: 'image/jpeg,image/png,application/pdf',
				withCredentials: true,
				uploadMultiple: false,
				paramName: 'upload_file',
				maxFilesize: 2.0,
				addRemoveLinks: false,
				thumbnailMethod: 'contain',
				dictDefaultMessage: 'Drop a file here, or click to find a file...',
				params: {
					fd_token: this.$getCookie('dashboard_token'),
					source: 'RANGE_MEMBERSHIPS',
					context: 'CONTACT_ATTACHMENTS',
					contact_id: this.listDetail.contact.contact_id
				}
			}
		}
	},
	watch: {
		contact_membership_id: function() {
			this.getItem(this.contact_membership_id)
		}
	},
	methods: {
		showPaymentButton(payment) {
			return payment.amount > 0 && payment.gateway_response.indexOf('REFUNDED') < 0 && payment.gateway_response.indexOf('REPROCESSED') < 0
		},
		showPaymentLabel(payment) {
			let gatewayResponse = JSON.parse(payment.gateway_response)
			return gatewayResponse.status.capitalizeWords()
		},
		showWaiver(waiver) {
			if (waiver.contact_waiver_id) {
				this.showWaiverDetail = waiver
				this.showAttachments = false
			} else {
				this.apiCall({ destination: 'get_item_application_pdf', data: { contact_id: waiver.contact_id } }).then(obj => {
				    if (obj.status == 'success' && obj.pdf_url) {
						// open pdf_url in a new window
						window.open(obj.pdf_url, '_blank')
				    }
				})
			}
		},
		saveWaiverDate() {
			if (!this.saving) {
				if (this.waiverOnFileDate == '') {
					this.waiverOnFile = false
				} else {
					this.waiverOnFile = true
				}
				this.saving = true
				this.message = 'Saving waiver date...'
				this.apiCall({ destination: 'save_printed_waiver_date', data: { contact_id: this.listDetail.contact.contact_id, waiver_date: this.waiverOnFileDate } }).then(obj => {
					this.saving = false
					this.messageStatus = obj.status
				    if (obj.status == 'success') {
						this.message = 'Waiver date saved!'
						this.messageTimeout = 1500
				    } else {
						this.message = obj.message
				    }
				})
			}
		},
        filesUploadSuccess(file, response) {
            var obj = JSON.parse(response)
			if (obj.status != 'success') {
				this.attachmentsMessage = obj.message
			} else {
				if (this.saving) { return }
				this.saving = true
				this.message = 'Saving attachment...'
				this.messageStatus = ''
				this.messageTimeout = 0
				let data = {
					contact_id: this.listDetail.contact.contact_id,
					file_name: obj.file_name,
					file_type: obj.file_type
				}
				this.apiCall({ destination: 'save_contact_attachments', data: data }).then(obj => {
				    if (obj.status != 'success') {
				        this.message = obj.message
						this.saving = false
				    } else {
						this.message = 'Attachment saved!'
						this.saving = false
						this.messageTimeout = 3000
				        this.getContactAttachments()
				    }
					this.$refs.filesDropzone.removeAllFiles()
				})
			}
        },
        filesUploadError(file, message, xhr) {
			// console.log('filesUploadError')
			// console.log(file)
			// console.log(message)
			// console.log(xhr)
            this.attachmentsMessage = message
		},
        filesFileAdded(file) {
            console.log(file)
        },
		statusClass(item) {
			if (item.membership_inactive) {
				return 'cancelled'
			} else if (item.primary_contact_membership_id) {
				return 'additional_member'
			} else if (!item.subscription_id) {
				return 'no_subscription'
			} else if (item.vault_type == 'PREPAID') {
				return 'prepaid'
			} else if (item.subscription_suspended) {
				return 'cc_failed'
			} else if (item.expiration_year && item.expiration_year + '-' + item.expiration_month + '30' < this.today) {
				return 'cc_expired'
			} else if (item.end_date && item.end_date < this.today) {
				return 'membership_expired'
			} else {
				return 'active'
			}
		},
		renewalDate(item) {
			if (item.membership.end_date) {
				return this.$moment(item.membership.end_date).add(1, 'days').format('MM/DD/YYYY')
			}
		},
		clearFilters() {
			this.filters = {
				page: 1,
				search_term: '',
				membership_id: '',
				subscription: '',
				credit_card: '',
				membership_number: '',
				membership_expired: '',
				waiver: '',
				inactive: 0,
				do_export: 0
			}
			if (this.contact_membership_id) {
				this.getItem(this.contact_membership_id)
			} else {
				if (this.$route.query.new_member_from_contact_id) {
					this.showAddMember = true
				} else {
					if (this.$route.query.membership_id) {
						this.filters.membership_id = this.$route.query.membership_id
					}
					this.getList()
				}
			}
		},
		toggleFilter(which) {
			this.filters[which] = !this.filters[which]
			if (which == 'has_subscription' && this.filters[which]) {
				this.filters.no_subscription = 0
			}
			if (which == 'no_subscription' && this.filters[which]) {
				this.filters.has_subscription = 0
			}
			if (which == 'cc_expired' && this.filters[which]) {
				this.filters.cc_expires_soon = 0
			}
			if (which == 'cc_expires_soon' && this.filters[which]) {
				this.filters.cc_expired = 0
			}
			if (which == 'has_membership_number' && this.filters[which]) {
				this.filters.no_membership_number = 0
			}
			if (which == 'no_membership_number' && this.filters[which]) {
				this.filters.has_membership_number = 0
			}
			if (which == 'membership_expired' && this.filters[which]) {
				this.filters.membership_expires_soon = 0
			}
			if (which == 'membership_expires_soon' && this.filters[which]) {
				this.filters.membership_expired = 0
			}
			this.filters.page = 1
			this.getList()
		},
		getListMemberships() {
			this.apiCall({ destination: 'get_list_memberships', data: { include_inactive: true } }).then(obj => {
				if (obj.status == 'success') {
					this.memberships = obj.memberships
					this.subscriptionsEnabled = obj.subscriptions_enabled
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getList(page) {
			if (this.loading) { return }
			this.showDetail = false;
			this.loading = true
			if (page) {
				this.filters.page = page
			}
			this.apiCall({ destination: 'get_list_members', data: this.filters }).then(obj => {
				this.loading = false
				this.messageStatus = obj.status
				if (obj.legacy_link) {
					this.legacyLink = obj.legacy_link
				}
				if (this.filters.do_export) {
					this.message = 'Downloading CSV...'
					this.filters.do_export = 0
					// accommodate live server filecache latency
					setTimeout(() => {
						document.getElementById("download").src = obj.download_url
						this.gettingExport = false
						this.message = ''
					}, 2000)
				} else {
					if (obj.status == 'success') {
						this.list = obj.members
						if (obj.paging) {
							this.paging = obj.paging
						}
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		fetchDetailTab(detailTab, forceReload = false) {
			if (!this.contact_membership_id || this.contact_membership_id == 'new') { return }
			if (detailTab == this.showDetailTab) { return }
			switch (detailTab) {
				case 'member':
					break
				case 'membership':
					break
				case 'subscription':
					this.getSubscriptionPayments()
					break
				case 'attachments':
					this.getContactAttachments()
					break
				case 'user':
					break
			}
			this.showDetailTab = detailTab
		},
		getItemRoute(contactMembershipId) {
			this.$router.push('/range-memberships/members/' + contactMembershipId)
		},
		addMember() {
			this.addCustomer = {
				search_term: '',
				membership_id: ''
			}
			this.customerSearchList = []
			this.showAddMember = true
		},
		addAdditionalMember() {
			this.addCustomer = {
				search_term: '',
				membership_id: this.listDetail.membership.membership_id
			}
			this.customerSearchList = []
			this.showAddAdditionalMember = true
		},
		searchCustomers() {
			if (!this.addCustomer.membership_id || !this.addCustomer.search_term) { return }
			this.modalMessage = 'Searching...'
			this.apiCall({ destination: 'get_list_contacts', data: { search_term: this.addCustomer.search_term, non_member: true } }).then(obj => {
				if (obj.status == 'success') {
					this.modalMessage = ''
					this.customerSearchList = obj.list
					if (this.customerSearchList.length == 0) {
						this.modalMessage = 'No customers found.'
					} else {
						this.modalMessage = ''
					}
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		newMemberFromContactId(contactId) {
			if (!this.addCustomer.membership_id) { return }
			this.customerSearchList = []
			if (this.$route.query.new_member_from_contact_id) {
				this.listDetail.membership = {
					membership_id: this.addCustomer.membership_id,
					contact_id: this.$route.query.new_member_from_contact_id
				}
				this.showAddMember = false
				this.saveMembershipInfo()
			} else {
				this.$router.push('/range-memberships/members/new' + (contactId != 'new' ? '?contact_id=' + contactId : ''))
			}
		},
		addAdditionalMemberFromContactId(contactId) {
			if (this.saving) { return }
			this.saving = true
			this.modalMessage = 'Adding customer...'
			this.apiCall({ destination: 'save_item_additional_member', data: { contact_membership_id: this.contact_membership_id, additional_contact_id: contactId } }).then(obj => {
				this.saving = false
				this.modalMessage = obj.message
				if (obj.status == 'success') {
					this.customerSearchList = []
					this.getItem(this.contact_membership_id)
					this.showAddAdditionalMember = false
				}
			}).catch(err => {
				console.log(err)
			})
		},
		removeCustomer() {
			if (this.saving) { return }
			this.saving = true
			this.modalMessage = 'Removing customer...'
			this.apiCall({ destination: 'remove_item_additional_member', data: { contact_membership_id: this.contact_membership_id, additional_contact_membership_id: this.removeCustomerMembership.contact_membership_id } }).then(obj => {
				this.saving = false
				this.modalMessage = obj.message
				if (obj.status == 'success') {
					this.getItem(this.contact_membership_id)
					this.removeCustomerMembership = false
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getItem(member) {
			console.log('getItem')
			if (this.loading) { return }
			this.loading = true
			let contactMembershipId = ''
			if (typeof member === 'object') {
				contactMembershipId = member.contact_membership_id
			} else {
				if (member == 'reload') {
					contactMembershipId = this.contact_membership_id
				} else {
					contactMembershipId = member
				}
			}
			if (contactMembershipId == 'new') {
				this.addItem()
				return
			}
			for (let i = 0; i < this.list.length; i++) {
				if (this.list[i].contact_membership_id == contactMembershipId) {
					this.listIndex = i
					break
				}
			}
			this.apiCall({ destination: 'get_item_member', data: { contact_membership_id: contactMembershipId } }).then(obj => {
				this.loading = false
				this.message = obj.message
				if (obj.status == 'success') {
					this.listDetail = obj.member
					if (this.listDetail['contact']['waiver_on_file']) {
						this.waiverOnFile = true
						this.waiverOnFileDate = this.listDetail['contact']['waiver_on_file']
					}
					this.showDetail = true;
					if (!this.showDetailTab) {
						this.showDetailTab = 'member'
					}
					if (!this.listDetail.subscription.vault_id || this.listDetail.subscription.prepaid) {
						this.editLocked = false
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getContactAttachments() {
			this.apiCall({ destination: 'get_contact_attachments', data: { contact_id: this.listDetail.contact.contact_id } }).then(obj => {
				if (obj.status == 'success') {
					this.attachments = obj.file_reference
					this.attachmentsMessage = ''
					this.waivers = obj.waivers
				}
			})
		},
		getSubscriptionPayments() {
			this.subscriptionPayments = []
			this.apiCall({ destination: 'get_list_subscription_history', data: { contact_id: this.listDetail.contact.contact_id, membership_id: this.listDetail.membership.membership_id } }).then(obj => {
				if (obj.status == 'success') {
					this.subscriptionPayments = obj.subscription_payments
					this.orderPayments = obj.order_payments
				}
			}).catch(err => {
				console.log(err)
			})
		},
        removeFile(file) {
            this.apiCall({ destination: 'save_contact_attachments', data: { contact_id: this.listDetail.contact.contact_id, file_name: file.file_name, delete: true } }).then(obj => {
                if (obj.status == 'success') {
					this.getContactAttachments()
                } else {
					this.message = 'Attachment saved!'
					this.saving = false
					this.messageTimeout = 3000
				    this.getContactAttachments(this.listDetail.contact_id)
					this.$refs.filesDropzone.removeAllFiles()
				}
            })
        },
		setListDetail(detail) {
			this.listDetail.contact = detail
		},
		addItem() {
			this.showAddMember = false
			this.customerSearchTerm = ''
			this.customerSearchList = []
			this.listDetail = {
				contact: {
					contact_id: this.$route.query.contact_id,
					client_id: this.getClientId,
					notes: [{ note: '', note_type: 'MEMBER'}]
				},
				membership: {
					contact_id: this.$route.query.contact_id,
					membership_id: this.addCustomer.membership_id
				},
				subscription: {},
				user: {},
				waiver:	{}
			}
			if (this.$route.query.contact_id) {
				// is contact already a member?
				this.apiCall({ destination: 'get_item_contact_membership_id', data: { contact_id: this.$route.query.contact_id } }).then(obj => {
					if (obj.contact_membership_id) {
						this.loading = false
						this.message = "Customer is already a member, loading membership info..."
						this.messageStatus = 'alert'
						setTimeout(() => {
							this.getItemRoute(obj.contact_membership_id)
						}, 2500)
					} else {
						this.apiCall({ destination: 'get_item_contact', data: { contact_id: this.$route.query.contact_id } }).then(obj => {
							this.loading = false
							if (obj.status == 'success') {
								this.listDetail.contact = obj.contact
								this.showDetail = true;
								if (!this.showDetailTab) {
									this.showDetailTab = 'member'
								}
								this.message = "Review contact info and then click Save Member<br>to finish adding customer to membership type."
								this.messageStatus = 'alert'
								this.messageTimeout = 0
							}
						}).catch(err => {
							console.log(err)
						})
					}
				}).catch(err => {
					console.log(err)
				})
			} else {
				this.loading = false
				this.showDetail = true
				if (!this.showDetailTab) {
					this.showDetailTab = 'member'
				}
			}
		},
		cancelChangeMembership() {
			this.showConfirmChangeMembershipType = false
			this.getItem(this.contact_membership_id)
		},
		changeMembershipType() {
			console.log(this.listDetail.membership);
			if (this.saving) { return }
			this.saving = true 
			this.apiCall({ destination: 'save_item_contact_membership_id', data: { contact_membership_id: this.contact_membership_id, membership_id: this.listDetail.membership.membership_id } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.getItem(this.contact_membership_id)
					this.showConfirmChangeMembershipType = false
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveItem() {
			switch (this.showDetailTab) {
				case "member":
					this.saveContactInfo()
					break;
				case "membership":
					this.saveMembershipInfo()
					break;
				case "subscription":
					this.saveSubscription()
					break;
				case "account":
					this.saveUser()
					break;
			}
		},
		saveContactInfo() {
			if (this.saving) { return }
			// validate contact info
			// trim whitespace from all elements in contact
			for (let key in this.listDetail.contact) {
				if (typeof this.listDetail.contact[key] == 'string') {
					this.listDetail.contact[key] = this.listDetail.contact[key].trim()
				}
			}
			this.message = ''
			if (!this.listDetail.contact.first_name && !this.listDetail.contact.last_name && !this.listDetail.contact.company_name) {
				this.message = 'First and Last Name or Company Name are required.'
			} else if (!this.listDetail.contact.address_1) {
				this.message = 'Address is required.'
			} else if (!this.listDetail.contact.city) {
				this.message += 'City is required.'
			} else if (!this.listDetail.contact.state) {
				this.message += 'State is required.'
			} else if (!this.listDetail.contact.zip_code) {
				this.message += 'Zip Code is required.'
			} else if (!this.listDetail.contact.email_address) {
				this.message += 'Email Address is required.'
			} else if (!this.$isValidEmailAddress(this.listDetail.contact.email_address)) {
				this.message += 'Email Address is not valid.'
			}
			if (this.message) {
				this.message += "<br><br>Please correct the info and try again."
				this.messageStatus = 'invalid'
				return
			}
			this.saving = true
			this.message = 'Saving conact info'
			this.messageStatus = ''
			this.messageTimeout = 0
			this.apiCall({ destination: 'save_item_contact', data: this.listDetail.contact }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					if (this.contact_membership_id == 'new') {
						this.listDetail.membership.contact_id = obj.contact_id
						this.saveMembershipInfo();
					} else {
						this.message = 'Contact info saved!'
						this.messageTimeout = 3000
					}
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveMembershipInfo() {
			if (this.saving || !this.listDetail.membership.membership_id) { return }
			this.message = ''
			if (this.listDetail.membership.start_date && this.listDetail.membership.end_date && this.listDetail.membership.start_date > this.listDetail.membership.end_date) {
				this.message = 'End date cannot be earlier than start date.'
			}
			if (this.message) {
				this.message += "<br><br>Please correct the info and try again."
				this.messageStatus = 'invalid'
				return
			}
			this.saving = true
			this.message = 'Saving membership info'
			this.apiCall({ destination: 'save_item_contact_membership', data: this.listDetail.membership }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.message = 'Membership info saved!'
					this.messageTimeout = 3000
					if (!this.showDetail || this.contact_membership_id == 'new') {
						this.$router.push('/range-memberships/members/' + obj.contact_membership_id)
					}
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveSubscription() {
			if (this.saving || this.contact_membership_id == 'new') { return }
			// validate billing info
			this.message = ''
			this.messageTimeout = 0
			if (!this.listDetail.subscription.full_name) {
				this.message = 'Billing name is required.'
			} else if (!this.listDetail.subscription.address_1) {
				this.message = 'Billing address is required.'
			} else if (!this.listDetail.subscription.city) {
				this.message += 'City is required.'
			} else if (!this.listDetail.subscription.state) {
				this.message += 'State is required.'
			} else if (!this.listDetail.subscription.zip_code) {
				this.message += 'Zip Code is required.'
			} else if (!this.listDetail.subscription.email_address) {
				this.message += 'Email Address is required.'
			} else if (!this.$isValidEmailAddress(this.listDetail.subscription.email_address)) {
				this.message += 'Email Address is not valid.'
			}
			if (this.message) {
				this.message += "<br><br>Please correct and try again."
				this.messageStatus = 'invalid'
				return
			}
			this.saving = true
			this.message = 'Saving subscription...'
			if (!this.listDetail.subscription.start_date) {
				this.listDetail.subscription.start_date = this.today
			}
			let postObject = {
				contact_membership_id: this.contact_membership_id,
				subscription: {
					contact_id: this.listDetail.contact.contact_id,
					full_name: this.listDetail.subscription.full_name,
					address_1: this.listDetail.subscription.address_1,
					city: this.listDetail.subscription.city,
					state: this.listDetail.subscription.state,
					zip_code: this.listDetail.subscription.zip_code,
					email_address: this.listDetail.subscription.email_address,
					phone_number: this.listDetail.subscription.phone_number,
					start_date: this.listDetail.subscription.start_date,
					subscription_id: this.listDetail.subscription.subscription_id,
					subscription_frequency_id: this.listDetail.membership.subscription_frequency_id,
					subscription_day: this.listDetail.subscription.subscription_day,
					amount: this.listDetail.subscription.amount,
					vault_id: this.listDetail.subscription.vault_id,
					prepaid: this.listDetail.subscription.prepaid,
					reference_number: this.listDetail.subscription.reference_number,
					expiration_month: this.listDetail.subscription.expiration_month,
					expiration_year: this.listDetail.subscription.expiration_year,
					internal_use_only: this.listDetail.subscription.internal_use_only,
				}
			}
			this.apiCall({ destination: 'save_item_subscription', data: postObject }).then(obj => {
				this.saving = false
				if (obj.subscription_id) {
					this.listDetail.subscription.subscription_id = obj.subscription_id
				}
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.message = 'Subscription info saved!'
					this.editLocked = true
					this.toggleButton = false
					setTimeout(() => {
						this.message = ''
						console.log('getItem ' + this.contact_membership_id)
						this.getItem(this.contact_membership_id)
					}, 3000)
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		editCCInfo() {
			if (this.editLocked == false || this.toggleButton == true) {
				this.editLocked = true
				this.toggleButton = false
				this.getItem(this.contact_membership_id)
			}
			else if (this.editLocked == true || this.toggleButton == false) {
				this.editLocked = false
				this.toggleButton = true
				this.listDetail.subscription.reference_number = null
			}
		},
		saveUser() {
			console.log(this.listDetail.user)
			if (this.saving || this.contact_membership_id == 'new') { return }
			// validate user info
			this.message = ''
			this.messageTimeout = 0
			if (!this.listDetail.user.user_name || this.listDetail.user.user_name.length < 6) {
				this.message = 'User name with at least six characters is required.'
			} else if (this.listDetail.user.new_password && this.listDetail.user.new_password.length < 8) {
				this.message = 'Password with at least eight characters is required.'
			} else if (!this.listDetail.user.user_type_id) {
				this.message += 'User type is required.'
			}
			if (this.message) {
				this.message += "<br><br>Please correct and try again."
				this.messageStatus = 'invalid'
				return
			}
			this.saving = true
			this.message = 'Saving user...'
			this.listDetail.user.contact_id = this.listDetail.contact.contact_id
			this.apiCall({ destination: 'save_item_user', data: this.listDetail.user }).then(obj => {
				this.saving = false
				if (obj.user_id) {
					this.listDetail.user.user_id = obj.user_id
				}
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.message = 'User saved!'
					setTimeout(() => {
						this.message = ''
						this.getItem(this.contact_membership_id)
					}, 3000)
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		confirmInviteMember() {
			this.inviteMemberData = {
				contact_id: this.listDetail.contact.contact_id,
				first_name: this.listDetail.contact.first_name,
				last_name: this.listDetail.contact.last_name,
				email_address: this.listDetail.contact.email_address,
				user_name: this.listDetail.user.user_name || this.listDetail.contact.email_address,
				password: 'membership'
			}
			this.modalMessage = ''
			this.showInviteMember = true
		},
		inviteMember() {
			if (this.saving || !this.$isValidEmailAddress(this.listDetail.contact.email_address)) { return }
			if (!this.inviteMemberData.user_name) {
				this.modalMessage = 'Username is required.'
				return
			}
			if (!this.inviteMemberData.password || this.inviteMemberData.password.length < 8) {
				this.modalMessage = 'Password with at least eight characters is required.'
				return
			}
			this.modalMessage = 'Sending invite email...'
			this.saving = true
			this.apiCall({ destination: 'invite_member', data: this.inviteMemberData }).then(obj => {
				if (obj.status == 'success') {
					this.modalMessage = 'Member has been invited!'
					// this.listDetail.user.user_id = obj.member_user_id
					this.getItem(this.contact_membership_id)
					setTimeout(() => {
						this.saving = false
						this.modalMessage = ''
						this.showInviteMember = false
					}, 3000)
				} else {
					this.saving = false
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err) 
			})
		},
		saveCancelMembership() {
			if (this.saving) { return }
			this.modalMessage = ''
			this.saving = true
			if (this.listDetail.inactive) {
				this.modalMessage = 'Canceling membership'
			} else {
				this.modalMessage = 'Reactivating membership'
			}
			let setInactive = !this.listDetail.membership.membership_inactive
			this.apiCall({ destination: 'save_item_contact_membership_inactive', data: { contact_membership_id: this.contact_membership_id, inactive: setInactive } }).then(obj => {
				if (obj.status == 'success') {
					this.listDetail.membership.inactive = setInactive
					if (this.listDetail.membership.inactive) {
						this.modalMessage = 'Membership cancelled'
					} else {
						this.modalMessage = 'Membership reactivated'
					}
					setTimeout(() => {
						this.saving = false
						this.modalMessage = ''
						this.showCancelMembership = false
						this.listDetail.membership.membership_inactive = setInactive
						//this.backToList()
					}, 1500)
				} else {
					this.saving = false
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err) 
			})
		},
		confirmDoPayment(payment) {
			this.paymentDetail = payment
			if (!this.paymentDetail.contact_id) {
				this.paymentDetail.contact_id = this.listDetail.contact.contact_id
			}
			if (!this.paymentDetail.subscription_payment_id) {
				this.paymentDetail.do = 'process'
				this.paymentDetail.heading = 'Process payment'
			} else if (this.paymentDetail.response_code == '100') {
				this.paymentDetail.do = 'refund'
				this.paymentDetail.heading = 'Refund payment'
			} else {
				this.paymentDetail.do = 'retry'
				this.paymentDetail.heading = 'Retry payment'
			}
			this.paymentDetail.not_taxable = this.listDetail.membership.not_taxable
			this.showDoPayment = true
		},
		doPayment() {
			if (this.saving) { return }
			this.modalMessage = ''
			this.saving = true
			let destination = ''
			if (this.paymentDetail.do == 'refund') {
				destination = 'process_subscription_refund'
				this.modalMessage = 'Refunding payment...'
			} else {
				destination = 'process_vault_payment'
				this.modalMessage = 'Processing payment...'
			}
			this.apiCall({ destination: destination, data: this.paymentDetail }).then(obj => {
				if (obj.status == 'success') {
					if (this.paymentDetail.do == 'refund') {
						this.modalMessage = 'Payment refunded!'
					} else {
						this.modalMessage = 'Payment processed!'
					}
					this.getSubscriptionPayments()
					setTimeout(() => {
						this.modalMessage = ''
						this.showDoPayment = false
						this.paymentDetail = {}
						this.saving = false
					}, 2000)
				} else {
					this.saving = false
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err) 
			})
		},
		backToList() {
			this.showDetailTab = ''
			this.listDetail = {}
			this.paymentDetail = {}
			this.editLocked = true
			this.showDetail = false
			this.showAddMember = false
			this.$router.push('/range-memberships/members')
			this.getList()
		},
		doExport() {
			if (this.gettingExport) { return false }
			this.gettingExport = true
			this.message = 'Generating CSV for ' + this.paging.total_items + ' items...'
			this.filters.product_id = ''
			this.filters.do_export = true
			this.getList()
		},
		printTerms() {
			// open a new window
			let printWindow = window.open('', 'Print Terms')
			// set font of new window
			printWindow.document.write('<style>body { font-family: Arial, Helvetica, sans-serif; }</style>')
			// add ref waiver_content html to new window
			printWindow.document.write(this.$refs.terms_content.innerHTML)
			// print new window
			printWindow.print()
			// close window after print
			printWindow.close()
		},
		downloadContactAttachment(fileName, fileId) {
			this.apiCall({ destination: 'download_contact_attachment', data: { file_name: fileName, file_id: fileId } }).then(async obj => {
				if (obj.status === 'success' && obj.file_download_url) {
					const response = await fetch(obj.file_download_url)
					const blob = await response.blob()

					const link = document.createElement('a')
					const objectURL = URL.createObjectURL(blob)
					link.href = objectURL
					link.download = fileName

					document.body.appendChild(link)
					link.click()
					URL.revokeObjectURL(objectURL)
					document.body.removeChild(link)
					this.message = 'File downloaded' 
				} else {
					this.message = obj.message
					this.attachments = this.attachments.filter(attachment => fileId !== attachment.contact_attachment_id)
				}
			})
			.catch(error => {
				this.message = 'Error fetching download URL: ' + error
			});
			this.message = ''
		},
		confirmRemoveFile(fileId) {
			this.messageTimeout = 0
			this.removeFileId = fileId;
			this.messageStatus = 'delete'
			this.message = 'This will permanently delete the file. Are you sure you want to proceed?'
		},
		removeContactAttachment() {
			const fileId = this.removeFileId;
			this.apiCall({ destination: 'remove_contact_attachment', data: { file_id: fileId } }).then(obj => {
				if (obj.status === 'success') {
					this.attachments = this.attachments.filter(attachment => fileId !== attachment.contact_attachment_id)
					this.message = 'Attachment removed' 
					this.messageTimeout = 3000;
				} else {
					this.message = obj.message
					this.messageTimeout = 3000;
				}
			})
			.catch(error => {
				this.message = 'Error removing file ' + error
				this.messageTimeout = 3000;
			});
			this.messageStatus = ''
			this.removeFileId = ''
		},
		doMessageModalAction() {
			switch (this.messageStatus) {
				case 'delete':
					if (this.removeFileId) {
						this.removeContactAttachment() 
					}
					break
			}
		},
		isNonRecurringSubscription(frequency) {
			const frequencies = ['NON-RECURRING', 'LIFETIME']
			return frequencies.includes(frequency)
		}
	}
}
</script>
<style lang="scss" scoped>
.member-list {
	td:first-child { padding: 0; }
		th:nth-child(2), td:nth-child(2) { padding-left: 0.5em; }
	.status-cell {
		position: relative;
		width: 1em;
		padding-right: 1em;
	}
	.status-expanded {
		position: absolute;
		display: none;
		top: 0;
		min-width: 7em;
		font-size: 0.8em;
		bottom: 0;
		align-items: center;
		grid-template-columns: 1fr;
		background: inherit;
		padding: 0 0.5em;
		justify-items: center;
	}
	.status-cell:hover .status-expanded {
		display: grid;
	}
	.cancelled { background: darkgray; .status-expanded { color: black; } }
	.additional_member { background: lightblue; .status-expanded { color: black; } }
	.cc_expired, .cc_failed { background: red; .status-expanded { color: white; } }
	.membership_expired { background: #F5B041; .status-expanded { color: white; } }
	.prepaid { background: lightgreen; .status-expanded { color: black; } }
	.no_subscription { background: lightgray; .status-expanded { color: black; } }
	.active { background: #4abd60; .status-expanded { color: black; } }
}

button.disabled {
	background: #D7DCE2;
}

.section {
	.detail-data {
		font-weight: bold;
		font-size: 1.15em;
	}
}
.section {
	position: relative;
	.disabled-pane {
		position: absolute;
		width: 100%;
		height: 100%;
		background-color: white;
		opacity: 0.6;
	}
}

.attachment-title {
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 0.25rem;
}

.one-two-columns {
	margin-top: 0px; 
	.attachment-preview {
		display: flex;
		justify-content: center;
		align-items: center;
		img {
			width: clamp(12.5rem, 10vw, 18.5rem);
			height: auto;
			object-fit: contain;
			padding-bottom: 1rem;
		}
	}
	.attachment-details {
		display: flex;
		justify-content: center;
		align-items: flex-start;
		flex-direction: column;
		padding: 1rem;
	}
	&:last-child {
		border-bottom: 1px solid rgb(204, 204, 204);
	}
}
</style>